import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { releaseService, projectService } from "../services/edosService";

const ENVIRONMENTS = ["dev", "qa", "uat", "staging", "production"];

export default function ReleaseCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const defaultProjectId = searchParams.get("projectId") || "";

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    projectId: defaultProjectId,
    releaseName: "",
    environment: "dev",
    plannedReleaseDate: "",
    description: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    if (!form.projectId || !form.releaseName || !form.environment) {
      toast({
        title: "Project, release name and environment are required",
        status: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await releaseService.create(form);
      const created = res?.data?.data || res?.data?.release || res?.data;
      toast({ title: "Release created", status: "success" });

      if (created?._id) {
        navigate(`/edos/releases/${created._id}`);
      } else {
        navigate(`/edos/releases?projectId=${form.projectId}`);
      }
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to create release",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={6}>
      <HStack mb={5} spacing={3}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={() =>
            navigate(
              `/edos/releases${form.projectId ? `?projectId=${form.projectId}` : ""}`,
            )
          }
        >
          Back
        </Button>
        <Text fontSize="2xl" fontWeight="bold">
          Create Release
        </Text>
      </HStack>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        maxW="760px"
      >
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>Project</FormLabel>
            <Select
              value={form.projectId}
              onChange={(e) =>
                setForm((f) => ({ ...f, projectId: e.target.value }))
              }
            >
              <option value="">Select project...</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Release Name</FormLabel>
            <Input
              value={form.releaseName}
              onChange={(e) =>
                setForm((f) => ({ ...f, releaseName: e.target.value }))
              }
              placeholder="e.g. v2.4.0"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Environment</FormLabel>
            <Select
              value={form.environment}
              onChange={(e) =>
                setForm((f) => ({ ...f, environment: e.target.value }))
              }
            >
              {ENVIRONMENTS.map((env) => (
                <option key={env} value={env}>
                  {env}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Planned Release Date</FormLabel>
            <Input
              type="date"
              value={form.plannedReleaseDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, plannedReleaseDate: e.target.value }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Input
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              placeholder="Release notes / scope"
            />
          </FormControl>

          <HStack justify="flex-end" pt={2}>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(
                  `/edos/releases${form.projectId ? `?projectId=${form.projectId}` : ""}`,
                )
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="green"
              isLoading={submitting}
              onClick={handleCreate}
            >
              Create Release
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}
